import Service, { inject as service } from '@ember/service';
import { getOwner } from '@ember/application';
import { isEmpty, isNone } from '@ember/utils';
import { buildWaiter } from '@ember/test-waiters';

import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import AccountService from 'cio/services/account';
import ApplicationAdapter from 'cio/adapters/application';
import AuthService from 'cio/services/auth';
import { DataCenter } from 'cio/models/account';
import { getHostForDataCenter } from 'cio/utils/data-centers';
import RavenService from 'cio/services/raven';
import { buildQueryString } from 'cio/utils/url-builder';
import { isEmptyOrEmptyObject, pruneObject } from 'cio/utils/objects';

export interface ApiErrorSource {
  pointer: string;
}

export interface ApiError {
  code?: string;
  status: number | string;
  detail: string;
  source?: ApiErrorSource;
}

export interface ApiResponse {
  status?: number | string;

  // TODO: can object form be ever returned now? if no, update the type and code referencing it
  errors?: ApiError[] | { [key: string]: string[] };
}

export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: RequestMethod;
  headers?: { [key: string]: string };
  includeStatus?: boolean;
  ignoreErrors?: boolean;
  data?: any;
  dataCenter?: DataCenter;
  pathScope?: 'environment' | 'account' | 'hydra_account' | 'global';
  replaceEnvironmentID?: string;
  replaceAccountID?: string;
  removeUrlNamespace?: boolean;
  processData?: boolean;
  contentType?: string;
  success?: (payload: any, textStatus: string, jqXHR: JQuery.jqXHR) => void;
  error?: (jqXHR: JQuery.jqXHR) => void;
  complete?: (jqXHR: JQuery.jqXHR, textStatus: string) => void;
  traditional?: boolean;
}

const WAITER = buildWaiter('api-service-request');
const EMPTY_STATUSES = [204];

class ApiResponseError extends Error {
  originalError: Error;

  constructor(message: string, originalError: Error) {
    super(message);

    this.originalError = originalError;
  }
}

function handleEmptyResponse(res: Response, resolve: (value: ApiResponse & any) => void, includeStatus = false) {
  let payload: ApiResponse = {};

  if (includeStatus) {
    payload.status = res.status;
  }

  return resolve(payload);
}

async function handleJSONResponse<Data = any>(
  res: Response,
  resolve: (value: Data | PromiseLike<Data>) => void,
  reject: (reason?: any) => void,
  includeStatus = false,
): Promise<void> {
  try {
    let data = await res.json();
    let payload: ApiResponse & Data = data;

    if (includeStatus) {
      payload.status = data.errors?.[0]?.status ?? res.status;
    }

    // 4XX and 5XX don't throw errors in `fetch`, so we need to handle those
    // here instead
    if (!res.ok) {
      // Some server errors won't return with a body. In those cases, we
      // should provide some sort of error, even if it's vague
      if (isEmptyOrEmptyObject(data)) {
        payload.errors = [
          {
            status: res.status,
            detail: res.statusText,
          },
        ];
      }

      return reject(payload);
    }

    return resolve(payload);
  } catch (err) {
    return reject(new ApiResponseError('Unable to parse JSON response', err));
  }
}

type TextRequestResponse = ApiResponse & { body?: string };

// This is almost certainly a `204`, but we'll handle text just in case
async function handleTextResponse(
  res: Response,
  resolve: (value: TextRequestResponse | PromiseLike<TextRequestResponse>) => void,
  reject: (reason?: any) => void,
  includeStatus = false,
): Promise<void> {
  try {
    let body = await res.text();
    let payload: TextRequestResponse = {};

    if (includeStatus) {
      payload.status = res.status;
    }

    if (!isEmptyOrEmptyObject(body)) {
      payload.body = body;
    }

    if (!res.ok) {
      payload.errors = [
        {
          status: res.status,
          detail: res.statusText,
        },
      ];

      return reject(payload);
    }

    return resolve(payload);
  } catch (err) {
    return reject(new ApiResponseError('Unable to parse text response', err));
  }
}

export default class ApiService extends Service {
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service account!: AccountService;
  @service auth!: AuthService;
  @service raven!: RavenService;

  request<Data = any>(path: string, options: RequestOptions = {}): Promise<ApiResponse & Data> {
    type RequestResponse = ApiResponse & Data;

    let owner = getOwner(this);
    let adapter = owner.lookup('adapter:application') as ApplicationAdapter;
    let abortController = new AbortController();
    let { body, contentType, query } = this.processData(options);
    let fetchOptions: RequestInit = {
      method: options.method ?? 'GET',
      headers: pruneObject({
        ...adapter.headers,
        ...options.headers,
        'Content-Type': contentType,
      }),
      body,
      signal: abortController.signal,
    };

    options.pathScope = options.pathScope ?? 'environment';

    let url = this.buildURL(adapter, path, query, options);

    let { includeStatus, ignoreErrors } = options;
    let asyncToken = WAITER.beginAsync();

    // We can safely disable this lint error. The reasons for this lint are:
    // - uncaught errors won't use `resolve` or `reject`
    //   - errors are caught and `resolve` and `reject` are used properly
    // - usually an anti-pattern and you don't need the Promise constructor
    //   - the api errors service needs to observe the promise, so we can't just
    //     await in the main body of the `request` function
    //
    /* eslint-disable-next-line no-async-promise-executor */
    let promise = new Promise<any>(async (resolve, reject) => {
      try {
        let res = await fetch(url, fetchOptions);
        let contentType = res.headers.get('content-type');
        let contentLength = res.headers.get('content-length');

        // The back end will correctly not send a body for `204` responses. They
        // will, however, set `Content-Type` to `application/json`, which causes
        // this to try and parse an empty body as JSON, which fails. In that
        // case, or when we know ahead of time that the body is empty, we should
        // avoid trying to parse the body as anything and return an empty object
        if (EMPTY_STATUSES.includes(res.status) || contentLength === '0') {
          handleEmptyResponse(res, resolve, includeStatus);
        } else if (
          (contentType?.startsWith('text/plain') ?? false) ||
          (contentType?.startsWith('text/html') ?? false)
        ) {
          // text/html is almost always an error page being returned from the server
          // and while we'll use the text handler, it will reject because the req
          // is not ok
          await handleTextResponse(res, resolve, reject, includeStatus);
        } else if (contentType?.startsWith('application/json')) {
          await handleJSONResponse(res, resolve, reject, includeStatus);
        } else {
          throw new Error(`Unexpected response type ${contentType ?? 'unknown'} with status ${res.status}`);
        }
      } catch (err) {
        // These errors are either `AbortError` or `TypeError` and thus represent
        // either a cancellation or something wrong in creating the request
        //
        // More info: https://developer.mozilla.org/en-US/docs/Web/API/fetch#exceptions

        // This is an AbortError caused by cancelling the promise or aborting
        // the fetch request
        if (err instanceof DOMException && err.name === 'AbortError') {
          return resolve({} as RequestResponse);
        }

        // These errors are from our response body handling and should be
        // passed straight through
        if (err instanceof ApiResponseError) {
          return reject(err);
        }

        // Any other errors mean that the `fetch` request couldn't be made for
        // some unknown reason. This could be a bad network connection, offline,
        // malformed request, or something else. These errors are generally
        // unhelpful and quite hard to debug.
        this.raven.withScope((scope) => {
          scope.setExtra('requestOptions', {
            url,
            method: fetchOptions.method,
            headers: fetchOptions.headers,
          });
          this.raven.captureException(err, scope);
        });

        return reject(new Error(`Unable to make HTTP request: ${err.message}`));
      }
    }).finally(() => {
      // Abort any unresolved fetch requests
      abortController.abort();
      WAITER.endAsync(asyncToken);
    });

    this.auth.refreshAuthCookies();

    if (!ignoreErrors) {
      this.apiErrors.attach(promise as any);
    }

    return promise;
  }

  post<Data = any>(path: string, options: RequestOptions = {}): Promise<Data> {
    return this.request<Data>(path, { ...options, method: 'POST' });
  }

  put<Data = any>(path: string, options: RequestOptions = {}): Promise<Data> {
    return this.request<Data>(path, { ...options, method: 'PUT' });
  }

  delete<Data = any>(path: string, options: RequestOptions = {}): Promise<Data> {
    return this.request<Data>(path, { ...options, method: 'DELETE' });
  }

  private buildURL(
    adapter: ApplicationAdapter,
    path: string,
    query: string | null | undefined,
    options: RequestOptions,
  ): string {
    let { host, namespace } = adapter;
    if (isNone(host)) {
      host = '';
    }

    // If there's a dataCenter field passed in, that overrides the adapter's host.
    if (options.dataCenter) {
      host = getHostForDataCenter(options.dataCenter);
    }

    let urlComponents = [`${host}/${namespace}`];

    if (options.removeUrlNamespace === true || options.pathScope === 'hydra_account') {
      urlComponents = [host];
      delete options.removeUrlNamespace;
    }

    if (options.pathScope === 'environment') {
      let environmentID = this.environment.current.id;
      if (options.replaceEnvironmentID) {
        environmentID = options.replaceEnvironmentID;
      }
      let environmentPrefix = `environments/${environmentID}`;

      urlComponents.push(environmentPrefix);
    } else if (options.pathScope === 'account') {
      let accountID: string | undefined;
      if (options.replaceAccountID) {
        accountID = options.replaceAccountID;
      } else {
        accountID = this.account.current.id;
      }

      let accountPrefix = `accounts/${accountID}`;

      urlComponents.push(accountPrefix);
    } else if (options.pathScope === 'hydra_account') {
      let accountID = this.account.current.id;
      if (options.replaceAccountID) {
        accountID = options.replaceAccountID;
      }

      let hydraAccountPrefix = `hydra/accounts/${accountID}`;

      urlComponents.push(hydraAccountPrefix);
    }

    delete options.pathScope;
    delete options.replaceEnvironmentID;

    urlComponents.push(path);

    let url = urlComponents.join('/');

    if (!isEmpty(query)) {
      let existingQuery = url.includes('?');
      let queryStart = existingQuery ? `&` : '?';

      url = `${url}${queryStart}${query}`;
    }

    return url;
  }

  private processData(options: RequestOptions = {}): {
    body?: RequestInit['body'];
    contentType?: string;
    query?: string;
  } {
    if (!isEmpty(options.data)) {
      let method = options.method ?? 'GET';

      // Request body creation for POST, PUT, DELETE, etc. Always JSON!
      if (!['HEAD', 'GET'].includes(method)) {
        return {
          body: JSON.stringify(options.data),
          contentType: 'application/json',
        };
      }

      if (method === 'GET') {
        return {
          query: buildQueryString(options.data, false),
        };
      }
    }

    return {};
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    api: ApiService;
  }
}
